import React, { useState, useEffect } from 'react';
import T from 'components/T';
import {
  formValueSelector,
  change,
  submit,
  isValid as reduxFormIsValid,
} from 'redux-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { i18n } from '@international/mastodon-i18n';
import { M } from '@dashboard-experience/mastodon';
import { trackAnalyticsEvent } from '../../../actions';
import { ANALYTICS_EVENTS } from '../../../lib/analytics';
import UploadButton from '../../fields/UploadButton';
import { VERIFICATION_FORM_NAME } from '../../../constants';
import SimplePanel from '../../Panels/SimplePanel';
import {
  DocumentListSection,
  OtherDocumentsSection,
  PreviouslyProvidedSection,
  DocumentUploadHeader,
  GenerateDateComponents,
  UploadCircleWrapper,
} from './DocumentVerificationComponents';
import Logo from '../../Logo';
import DocumentInfoModal from './DocumentInfoModal';
import CandidateExplanationModal from './CandidateExplanationModal';
import {
  extractDocumentRequirements,
  formatAcceptedTypesList,
  getTranslation,
  groupedDocuments,
  isDocumentCountSatisfied,
  splitDocuments,
  updateEnabledDocuments,
  uploadedFilesUpdate,
  onCancel,
  onSubmit,
  createOnRemoveByUploadId,
  SLOT_PINS_FIELD,
} from './utils/formatter';
import {
  DateObject,
  DocumentAccessProps,
  UploadSectionProps,
  SlotPins,
} from './utils/interfaces';
import { dateObject } from './utils/constants';
import { useNavSlotSetters } from '../VerificationBase/NavSlotContext';
import useHarnessFlag from '../../../api/harness/useHarnessFlag';
import { HARNESS_FLAGS } from '../../../api/harness/harnessFlags';

import './EmploymentMultidocPanel.scss';

const I18N_PREFIX = 'components.Verification.EmploymentMultidocPanel';

// Resolve the upload card's heading + instructions i18n keys. Off-flag keeps the
// legacy copy; on-flag the copy depends on whether the candidate previously
// provided docs/explanation — when they did, the card is reframed as a "try
// finding these documents again" prompt.
const resolveUploadCopyKeys = (
  aiOcrEnabled: boolean,
  showPreviouslyProvided: boolean,
) => {
  if (!aiOcrEnabled) {
    return {
      headingKey: 'buttons.uploadDocument',
      instructionsKey: `${I18N_PREFIX}.upload`,
    };
  }
  return showPreviouslyProvided
    ? {
        headingKey: `${I18N_PREFIX}.tryFindingAgainHeading`,
        instructionsKey: `${I18N_PREFIX}.tryFindingAgainBody`,
      }
    : {
        headingKey: `${I18N_PREFIX}.uploadEmploymentHeading`,
        instructionsKey: `${I18N_PREFIX}.uploadInstructionsV2`,
      };
};

const UploadSection: React.FC<UploadSectionProps> = ({
  documents,
  model,
  policy,
  updateDocuments,
  getDocuments,
  currentDocumentsRequired,
  setCurrentDocumentsRequired,
  setDocuments,
  documentTypes,
  additionalDocuments,
  onRemoveByUploadId,
  aiOcrEnabled,
  screeningId,
  company,
  fullName,
  isYearly,
  startDate,
  previouslyProvided,
  candidateDocContextEnabled = false,
  slotPins = {},
  updateSlotPins,
}) => {
  const dispatch = useDispatch();
  const { setNavSlot, setSuppressSubmit, setDisablePrevious } =
    useNavSlotSetters();
  const isFormValid = useSelector((state: any) =>
    reduxFormIsValid(VERIFICATION_FORM_NAME)(state),
  );
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [candidateExplanation, setCandidateExplanation] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  // EVERI-4005: counts open candidate-context editors — Previous/"I don't have..." disable while any is open.
  const [openContextEditorCount, setOpenContextEditorCount] = useState(0);
  const isEditingContext = openContextEditorCount > 0;
  const handleContextEditingChange = (isEditing: boolean) =>
    setOpenContextEditorCount(prev => Math.max(0, prev + (isEditing ? 1 : -1)));
  // EVERI-3325: on-flag, the explanation modal ("I don't have the required
  // documents") finalizes on its own confirm — there is no page-level Submit in
  // that partial state. Its confirm sets `pendingSubmit` and the effect below
  // fires the verification-form submit once the form is valid (the committed
  // explanation satisfies the gate). Gating on validity instead of submitting
  // inline avoids racing redux-form's re-validation after the commit. (When all
  // required docs are satisfied instead, the page reveals a Submit button the
  // candidate clicks directly — that path does not use `pendingSubmit`.)
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const {
    documentsRequired,
    documentsProvided,
    remainingDocuments: allRemainingDocuments,
    optionalDocuments,
  } = groupedDocuments(currentDocumentsRequired);
  const exceptionId = optionalDocuments[0]?.id ?? '';
  const additionalDocumentsWithException = additionalDocuments.map(file => ({
    ...file,
    exceptionId,
  }));
  // EVERI-4005: exclude docs already pinned to a required row so they aren't also shown under "Other documents".
  const otherDocuments = additionalDocumentsWithException.filter(
    doc => !(doc as any).handle || !slotPins?.[(doc as any).handle],
  );
  // EVERI-4005: a pinned row releases Continue like a real match — the document's own year still isn't verified server-side.
  const pinnedYears = new Set(
    additionalDocumentsWithException
      .filter(doc => (doc as any).handle && slotPins?.[(doc as any).handle])
      .map(doc => slotPins[(doc as any).handle]),
  );
  const remainingDocuments = allRemainingDocuments.filter(
    doc => !pinnedYears.has(doc.value),
  );
  // EVERI-4005: package setting — enough documents in total unlocks Continue even if a specific year is still empty. On-flag only, so off-flag/legacy accounts keep today's per-year gate.
  const documentCountSatisfied = isDocumentCountSatisfied(
    candidateDocContextEnabled,
    documentsProvided,
    optionalDocuments,
    documentsRequired,
  );
  const requirementsSatisfied =
    remainingDocuments.length === 0 || documentCountSatisfied;
  // EVERI-4005: requireMultiDocuments ignores the field's own value — every upload button (global or per-row) shares this one gate.
  const shouldSubmitUpload =
    requirementsSatisfied ||
    (candidateExplanation.length > 0 && !isExplanationModalOpen);
  const handleUploadSuccess = (res: { filesUploaded: File[] }) => {
    setUploadedFiles(res.filesUploaded);
    setDocuments([...documents, ...res.filesUploaded]);
    updateDocuments(model, undefined);
    res.filesUploaded.length > 0 && setIsDocumentModalOpen(true);
  };

  // EVERI-4005: same as handleUploadSuccess, plus pins the upload's handle to this row's year for rendering only.
  const handleRowUploadSuccess = (
    res: { filesUploaded: any[] },
    row: DateObject,
  ) => {
    handleUploadSuccess(res);
    // EVERI-4005: mirrors handleUploadSuccess's own field clear — otherwise a withdrawn file stays attached to this row's field and ships anyway.
    updateDocuments(`${model}-row-${row.id}`, undefined);
    if (!res.filesUploaded?.length || !updateSlotPins) return;
    const newPins = res.filesUploaded.reduce<SlotPins>((acc, file) => {
      if (file?.handle) acc[file.handle] = row.value;
      return acc;
    }, {});
    updateSlotPins({ ...slotPins, ...newPins });
  };

  // EVERI-4005: Edit/Save from the list — patches only candidate_context, never year/type.
  const handleSaveContext = (
    uploadId: string,
    contextExceptionId: string | undefined,
    value: string,
  ) => {
    if (!contextExceptionId) return;
    const patchContext = (doc: any) => {
      if (doc.uploadId !== uploadId) return doc;
      const { candidate_context: _drop, ...restAttributes } =
        doc.metadata?.context_attributes || {};
      return {
        ...doc,
        metadata: {
          ...doc.metadata,
          context_attributes: {
            ...restAttributes,
            ...(value && { candidate_context: value }),
          },
        },
      };
    };
    const bucket = getDocuments(contextExceptionId) || {};
    const updatedBucket = Object.fromEntries(
      Object.entries(bucket).map(([docType, docs]) => [
        docType,
        (docs as any[]).map(patchContext),
      ]),
    );
    updateDocuments(contextExceptionId, updatedBucket);
    // EVERI-4005: otherDocuments/pinnedDocument render from local `documents` state, not the bucket — without this the editor reverts to the old value after Save.
    setDocuments((prevDocuments: any[]) => prevDocuments.map(patchContext));
  };

  const handleModalUploadSuccess = (res: { filesUploaded: any[] }) => {
    const dateObjects: DateObject[] = [];
    const filesWithExceptionId = res.filesUploaded.map(file => {
      const dateObj = {
        ...dateObject(exceptionId, file.uploadId),
        exceptionId,
      };
      dateObjects.push(dateObj);

      return {
        ...file,
        id: file?.uploadId,
        exceptionId,
      };
    });
    setDocuments([...documents, ...filesWithExceptionId]);
    handleUploadedFilesUpdate(dateObjects, filesWithExceptionId);
    // EVERI-4005: unlike a row/global upload, this modal-upload path never fed currentDocumentsRequired, so isDocumentCountSatisfied couldn't see these files.
    setCurrentDocumentsRequired((prev: DateObject[]) => [
      ...prev,
      ...dateObjects,
    ]);
  };

  // TO DO: view onRemoveByUploadId definition
  useEffect(() => {
    setUploadedFiles(prevFiles => {
      const remainingFiles = prevFiles.filter(file =>
        documents.some(doc => doc.uploadId === file.uploadId),
      );
      return remainingFiles;
    });
  }, [documents]);

  const analyticsPayload = {
    documentsRequired: documentsRequired.length,
    documentsProvided: documentsProvided.length,
    policy,
  };
  const handleSubmitExplanation = async () => {
    const updated = {
      ...(getDocuments(exceptionId) || []),
      candidate_explanation: candidateExplanation,
    };
    await updateDocuments(exceptionId, updated);
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_EXPLANATION_SUBMITTED,
        analyticsPayload,
      ),
    );
  };

  const handleUploadedFilesUpdate = (stateYears: any, uploadedDocs: any) => {
    return uploadedFilesUpdate(
      uploadedDocs,
      stateYears,
      updateDocuments,
      getDocuments,
    );
  };

  const handleDocumentInfoCancel = () => {
    // restricts cancelation to verified documents (ignore unverified ones)
    onCancel(setIsDocumentModalOpen, setDocuments, uploadedFiles);
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_DOCUMENTS_CLOSED,
        analyticsPayload,
      ),
    );
  };

  const handleDocumentInfoSubmit = () => {
    // EVERI-3325: confirming the review-documents modal just closes it and
    // returns the candidate to the upload page. It does NOT auto-submit — once
    // every required doc is satisfied the page reveals a Submit button the
    // candidate clicks to finalize; if docs are still outstanding they stay on
    // the page with the "I don't have the required documents" link. (The
    // explanation modal still finalizes on its own confirm via requestSubmit,
    // because the partial state has no page-level Submit.)
    onSubmit(setIsDocumentModalOpen);
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_DOCUMENTS_LABELED,
        analyticsPayload,
      ),
    );
  };

  useEffect(() => {
    const uploadedIds = documents.map(({ uploadId }) => uploadId);
    const updated = updateEnabledDocuments(
      currentDocumentsRequired,
      uploadedIds,
    );
    setCurrentDocumentsRequired(updated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    dispatch(
      trackAnalyticsEvent(
        ANALYTICS_EVENTS.MULTIDOC_PAGE_VIEWED,
        analyticsPayload,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const requiredTypes = documentTypes
    .filter(doc => doc.requiredForVerification)
    .map(doc => getTranslation(doc.type))
    .join(', ');

  // On-flag, when the candidate provided apply-time docs/explanation (the BE
  // `previouslyProvided` payload), reframe the upload card as a "try again"
  // prompt and surface the read-only "What you previously provided" block.
  // Off-flag, or when nothing was previously provided, the card keeps its
  // existing framing untouched (no regression / no empty sections).
  const showPreviouslyProvided = aiOcrEnabled && Boolean(previouslyProvided);
  // Oxford "X, Y, or Z" join of the accepted document types, interpolated into
  // the "What you still need to provide" helper line per the locked mock.
  const acceptedTypesList = formatAcceptedTypesList(documentTypes);

  const {
    headingKey: uploadHeadingKey,
    instructionsKey: uploadInstructionsKey,
  } = resolveUploadCopyKeys(aiOcrEnabled, showPreviouslyProvided);

  const requestedDocumentsHeading = (
    <h2>
      <T
        value='components.employment.requestedDocuments'
        className='text-panel'
      />
    </h2>
  );

  const requestedDocumentsList = (
    <GenerateDateComponents
      documentsRequired={documentsRequired}
      policy={policy}
      documents={documents}
      onRemoveByUploadId={onRemoveByUploadId}
      isYearly={isYearly}
      startDate={startDate}
      boxed={aiOcrEnabled}
      uniformLabel={aiOcrEnabled}
      model={model}
      candidateDocContextEnabled={candidateDocContextEnabled}
      onRowUploadSuccess={handleRowUploadSuccess}
      onSaveContext={handleSaveContext}
      onContextEditingChange={handleContextEditingChange}
      slotPins={slotPins}
      additionalDocuments={additionalDocumentsWithException}
      shouldSubmit={shouldSubmitUpload}
    />
  );

  const missingDocumentsButton = !requirementsSatisfied && (
    <M.Button
      onClick={
        isEditingContext ? undefined : () => setIsExplanationModalOpen(true)
      }
      disabled={isEditingContext}
      className='unset-pointer'
    >
      <T
        value='components.employment.missingDocuments'
        className='document-upload-header'
      />
    </M.Button>
  );

  // EVERI-3323: on-flag, the "I don't have the required documents" link lives
  // in the page-nav row (next to Previous) via the NavSlot context, not in the
  // card. Clear the slot off-flag, when nothing is missing, or on unmount.
  // Off-flag keeps rendering the link inside the card (legacy layout).
  useEffect(() => {
    if (!aiOcrEnabled || requirementsSatisfied) {
      setNavSlot(null);
      return undefined;
    }
    // Reuse the single missingDocumentsButton definition (it's the button here
    // since we've passed the requirementsSatisfied early return) so the
    // on-flag nav-slot link and the off-flag in-card link can't drift.
    setNavSlot(
      <div className='multidoc-nav-missing-docs'>{missingDocumentsButton}</div>,
    );
    return () => setNavSlot(null);
    // missingDocumentsButton is intentionally omitted — it's rebuilt from requirementsSatisfied/isEditingContext (both already deps) on every render; its onClick uses stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiOcrEnabled, requirementsSatisfied, isEditingContext, setNavSlot]);

  // EVERI-3325: on-flag, the page-nav action toggles with required-doc state.
  // While any required doc is still outstanding, hide the page-level Submit and
  // show the "I don't have the required documents" link instead (registered
  // into the nav slot above). Once every required doc is satisfied, reveal
  // Submit so the candidate can finalize from the page. Off-flag keeps today's
  // always-rendered Submit.
  useEffect(() => {
    // EVERI-4005: also suppress while a candidate-context editor or either review modal is open — the fixed nav bar renders above their overlays, so Submit stayed clickable underneath and could fire before an in-flight upload/explanation was ever confirmed. Scoped to aiOcrEnabled like the base clause — off-flag has no nav-slot swap at all (Submit always renders there), and CandidateExplanationModal's off-flag confirm synchronously clicks that Submit node right after closing this modal, before an unscoped suppress could ever clear in time.
    setSuppressSubmit(
      aiOcrEnabled &&
        (!requirementsSatisfied ||
          isEditingContext ||
          isDocumentModalOpen ||
          isExplanationModalOpen),
    );
    return () => setSuppressSubmit(false);
  }, [
    aiOcrEnabled,
    requirementsSatisfied,
    isEditingContext,
    isDocumentModalOpen,
    isExplanationModalOpen,
    setSuppressSubmit,
  ]);

  // EVERI-4005: disable Previous while a candidate-context edit or either review modal is open — same fixed-nav-above-overlay issue as suppressSubmit, confirmed live: Previous navigated away and silently discarded an in-flight upload. Scoped to aiOcrEnabled like suppressSubmit — off-flag has never disabled Previous for any reason, and that's a separate, pre-existing gap, not something to change here.
  useEffect(() => {
    setDisablePrevious(
      aiOcrEnabled &&
        (isEditingContext || isDocumentModalOpen || isExplanationModalOpen),
    );
    return () => setDisablePrevious(false);
  }, [
    aiOcrEnabled,
    isEditingContext,
    isDocumentModalOpen,
    isExplanationModalOpen,
    setDisablePrevious,
  ]);

  // EVERI-3325: once a modal confirm has requested submission, fire the
  // verification-form submit as soon as the form becomes valid. The confirm
  // handlers only flip `pendingSubmit`; this decouples the submit from the
  // render in which the committing state change happened, so validation has
  // settled by the time we dispatch.
  useEffect(() => {
    if (aiOcrEnabled && pendingSubmit && isFormValid) {
      setPendingSubmit(false);
      dispatch(submit(VERIFICATION_FORM_NAME));
    }
  }, [aiOcrEnabled, pendingSubmit, isFormValid, dispatch]);

  return (
    <SimplePanel>
      <M.Grid>
        <M.GridRow>
          {/* EVERI-3323: on-flag use a full-width column — identical to the
              header card's intro-paragraph column and the document-rows column
              below — so the heading/instructions share the same left edge as
              "Provide the following documents…" AND span the full card width
              (the rows below need full width per the mock). Off-flag keeps
              today's md={12}. */}
          <M.GridCol
            {...(aiOcrEnabled ? { sm: 4, md: 8, lg: 16 } : { md: 12 })}
          >
            <h1 className={aiOcrEnabled ? 'multidoc-upload-heading' : ''}>
              {/* On-flag heading is "Try finding these documents again" when the
                  candidate previously provided docs/explanation, otherwise the
                  standard upload heading. */}
              <T
                value={uploadHeadingKey}
                company={company}
                className='text-panel'
              />
            </h1>
            {/* EVERI-3323: no divider between heading and instructions on-flag
                (matches the apply locked design). Off-flag keeps today's rule. */}
            {!aiOcrEnabled && <hr />}
            {/* On-flag intro is the "you were asked for these before" explainer
                (company bolded) when previously provided, otherwise the standard
                instructions. `company` is interpolated only by the try-again
                copy; the other keys ignore the extra prop. */}
            <T
              value={uploadInstructionsKey}
              documentTypes={requiredTypes}
              company={company}
              className={
                aiOcrEnabled
                  ? 'text-panel multidoc-upload-instructions'
                  : 'text-panel'
              }
              dangerousHTML
            />
          </M.GridCol>
        </M.GridRow>
        {/* EVERI-3323: the two <br/> spacers are the legacy (off-flag) gap
            between the heading block and the content row. On-flag the gap is
            controlled by `.multidoc-doc-rows` margin instead, so the
            instructions sit closer to the first document row. */}
        {!aiOcrEnabled && (
          <>
            <br />
            <br />
          </>
        )}
        {/* EVERI-3323: the single-column layout is the OCR-enabled experience.
            Multi-doc submission requires OCR, so a candidate only reaches a
            working flow with `aiOcrEnabled` true; when the EVERI-3908
            harness kill-switch forces manual entry, `aiOcrEnabled` is false
            and we keep the legacy 2-column layout + circular progress
            widget so there's no regression. */}
        {aiOcrEnabled ? (
          <M.GridRow>
            {/* EVERI-3323: full-width column matching the heading column above
                and the header card's intro paragraph, so the document rows +
                Upload button share the same left edge AND span the full card
                width (per the mock). Keep default column padding (no
                `no-padding`) so nothing shifts left of that edge. */}
            <M.GridCol sm={4} md={8} lg={16} className='text-left'>
              {/* When reframed as "try again", label the remaining required-doc
                  rows with the "What you still need to provide" sub-heading +
                  accepted-types helper (per the locked mock). */}
              {showPreviouslyProvided && (
                <div className='multidoc-still-need'>
                  <h2 className='multidoc-still-need__heading'>
                    <T
                      value='components.Verification.EmploymentMultidocPanel.whatYouStillNeed'
                      className='text-panel'
                    />
                  </h2>
                  <T
                    value='components.Verification.EmploymentMultidocPanel.whatYouStillNeedSubtext'
                    documentTypes={acceptedTypesList}
                    company={company}
                    className='text-panel multidoc-still-need__subtext'
                  />
                </div>
              )}
              <div className='multidoc-doc-rows'>{requestedDocumentsList}</div>
              {/* EVERI-4005: redundant once every row has its own upload control — kept for pre-flag accounts. */}
              {!candidateDocContextEnabled && (
                <div className='multidoc-upload-actions'>
                  <UploadButton
                    kind={
                      remainingDocuments.length > 0 ? 'primary' : 'secondary'
                    }
                    name={model}
                    hideIcon
                    buttonText='buttons.uploadDocuments'
                    buttonClass=''
                    context='multi-documents'
                    fileOptions={{ maxFiles: documentsRequired.length + 10 }}
                    handleUploadSuccess={handleUploadSuccess}
                    shouldSubmit={shouldSubmitUpload}
                  />
                </div>
              )}
              {/* EVERI-3323: on-flag, when the candidate has uploaded documents
                  that didn't satisfy a required slot (and the required docs are
                  therefore still outstanding, so the flow hasn't submitted), show
                  the "Other documents" block inside the card per the locked mock.
                  Gated on additionalDocuments so it never shows an empty block
                  and can't flash on the happy path (all required docs matched ->
                  submit exits). The submitted-explanation widget is intentionally
                  not restored — that flow exits immediately. */}
              {otherDocuments.length > 0 && (
                <OtherDocumentsSection
                  additionalDocuments={otherDocuments}
                  onRemoveByUploadId={onRemoveByUploadId}
                  candidateDocContextEnabled={candidateDocContextEnabled}
                  onSaveContext={handleSaveContext}
                  onContextEditingChange={handleContextEditingChange}
                />
              )}
              {/* Read-only "What you previously provided" block — apply-time
                  supporting docs + explanation. Self-guards to null when absent,
                  so it only renders with real data. */}
              {showPreviouslyProvided && (
                <PreviouslyProvidedSection
                  previouslyProvided={previouslyProvided}
                />
              )}
            </M.GridCol>
          </M.GridRow>
        ) : (
          <M.GridRow>
            <M.GridCol lg={8} className='no-padding text-left'>
              {requestedDocumentsHeading}
              {requestedDocumentsList}
            </M.GridCol>
            <M.GridCol lg={8} className='no-padding'>
              <SimplePanel>
                <div className='content-center'>
                  <UploadCircleWrapper
                    progress={
                      documentsProvided.length / documentsRequired.length
                    }
                  />
                  <T
                    value='components.employment.documentProgress'
                    progress={documentsProvided.length}
                    total={documentsRequired.length}
                    className='text-panel'
                  />
                  <UploadButton
                    kind='secondary'
                    name={model}
                    hideIcon
                    buttonText='buttons.uploadDocument'
                    buttonClass=''
                    context='multi-documents'
                    fileOptions={{ maxFiles: documentsRequired.length + 10 }}
                    handleUploadSuccess={handleUploadSuccess}
                    shouldSubmit={
                      documentsRequired.length <= documentsProvided.length ||
                      (candidateExplanation.length > 0 &&
                        !isExplanationModalOpen)
                    }
                  />
                  {missingDocumentsButton}
                </div>
              </SimplePanel>
            </M.GridCol>
          </M.GridRow>
        )}
        {isDocumentModalOpen && uploadedFiles && (
          <DocumentInfoModal
            files={uploadedFiles}
            documentsRequired={currentDocumentsRequired}
            onDocumentsRequiredChange={setCurrentDocumentsRequired}
            handleUploadedFilesUpdate={handleUploadedFilesUpdate}
            onCancel={handleDocumentInfoCancel}
            onSubmit={handleDocumentInfoSubmit}
            onRemoveByUploadId={onRemoveByUploadId}
            documentTypes={documentTypes}
            aiOcrEnabled={aiOcrEnabled}
            screeningId={screeningId}
            company={company}
            fullName={fullName}
            candidateDocContextEnabled={candidateDocContextEnabled}
          />
        )}
        {isExplanationModalOpen && (
          <CandidateExplanationModal
            model={model}
            // EVERI-4005: pin-aware — a pinned row is satisfied, so it shouldn't show as still-missing here.
            documentsRequired={remainingDocuments}
            policy={policy}
            candidateExplanation={candidateExplanation}
            setCandidateExplanation={setCandidateExplanation}
            setIsModalOpen={setIsExplanationModalOpen}
            handleSubmitExplanation={handleSubmitExplanation}
            // EVERI-4005: excludes docs already pinned to a row, same as the main panel's "Other documents".
            additionalDocuments={otherDocuments}
            onRemoveByUploadId={onRemoveByUploadId}
            handleModalUploadSuccess={handleModalUploadSuccess}
            isYearly={isYearly}
            startDate={startDate}
            aiOcrEnabled={aiOcrEnabled}
            requestSubmit={() => setPendingSubmit(true)}
          />
        )}
      </M.Grid>
    </SimplePanel>
  );
};

const EmploymentMultidocPanel: React.FC<DocumentAccessProps> = ({
  updateDocuments,
  getDocuments,
  model = '',
  verification = {},
  slotPins,
  updateSlotPins,
}) => {
  const [documents, setDocuments] = useState<DateObject[]>([]);
  const [aiOcrEnabled, setAiOcrEnabled] = useState<boolean>(false);
  // EVERI-3908: Harness kill-switch that forces the legacy (non-OCR) manual
  // entry path, overriding `aiOcrEnabled` back to false. The SDK key is
  // an anonymous UUID (no logged-in candidate identity at this point), so the
  // targeting rule matches on the `accountId` attribute instead. Split drops
  // the whole attribute bag if any value is undefined, so fall back
  // explicitly (verification defaults to `{}` before it loads).
  const { enabled: forceOcrManualEntry } = useHarnessFlag({
    flagKey: HARNESS_FLAGS.VERIFICATIONS_OCR_MANUAL_ENTRY,
    attributes: { accountId: verification?.accountId || 'empty-account-id' },
  });
  // EVERI-4005: same accountId targeting as above; gated on isResolved too so the UI can't flash in then disappear.
  const {
    enabled: candidateDocContextFlag,
    isResolved: candidateDocContextResolved,
  } = useHarnessFlag({
    flagKey: HARNESS_FLAGS.VERIFICATIONS_MULTIDOC_CANDIDATE_DOC_CONTEXT,
    attributes: { accountId: verification?.accountId || 'empty-account-id' },
  });
  const candidateDocContextEnabled =
    candidateDocContextFlag && candidateDocContextResolved;
  const onRemoveByUploadId = createOnRemoveByUploadId(
    setDocuments,
    getDocuments,
    updateDocuments,
  );

  const {
    company,
    companyLogoUri,
    documentsRequired,
    endDateFormatted,
    hiringPolicy,
    position,
    screeningId,
    startDateFormatted,
    type,
    fullName,
    isYearly,
    previouslyProvided,
  } = extractDocumentRequirements(verification);

  const [currentDocumentsRequired, setCurrentDocumentsRequired] =
    useState(documentsRequired);
  const [, additionalDocuments] = splitDocuments(
    documents,
    currentDocumentsRequired,
  );

  useEffect(() => {
    setAiOcrEnabled(!forceOcrManualEntry);
  }, [forceOcrManualEntry]);

  return (
    <div>
      <SimplePanel>
        {companyLogoUri && <Logo logoUri={companyLogoUri} />}
        <DocumentUploadHeader
          position={position}
          company={company}
          startDate={startDateFormatted}
          endDate={endDateFormatted}
        />
        <M.Grid>
          <M.GridRow>
            {/* EVERI-3323: on-flag use the same full-width span as the upload
                card's columns so this intro paragraph shares their left edge.
                Off-flag keeps today's md={12}. */}
            <M.GridCol
              {...(aiOcrEnabled ? { sm: 4, md: 8, lg: 16 } : { md: 12 })}
            >
              <T
                value='components.Verification.EmploymentMultidocPanel.text'
                className={
                  aiOcrEnabled ? 'text-panel multidoc-intro-text' : 'text-panel'
                }
                employerName={company}
                dangerousHTML
              />
            </M.GridCol>
          </M.GridRow>
        </M.Grid>
      </SimplePanel>
      <UploadSection
        documents={documents}
        setDocuments={setDocuments}
        model={model}
        policy={hiringPolicy}
        getDocuments={getDocuments}
        updateDocuments={updateDocuments}
        currentDocumentsRequired={currentDocumentsRequired}
        setCurrentDocumentsRequired={setCurrentDocumentsRequired}
        documentTypes={type}
        additionalDocuments={additionalDocuments}
        onRemoveByUploadId={onRemoveByUploadId}
        aiOcrEnabled={aiOcrEnabled}
        screeningId={screeningId}
        company={company}
        fullName={fullName}
        isYearly={isYearly}
        startDate={startDateFormatted}
        previouslyProvided={previouslyProvided}
        candidateDocContextEnabled={candidateDocContextEnabled}
        slotPins={slotPins}
        updateSlotPins={updateSlotPins}
      />
      {/* On-flag this section renders inside the upload card (see
          UploadSection); off-flag it stays its own card below. */}
      {!aiOcrEnabled && (
        <DocumentListSection
          additionalDocuments={additionalDocuments}
          onRemoveByUploadId={onRemoveByUploadId}
        />
      )}
    </div>
  );
};

const selector = formValueSelector(VERIFICATION_FORM_NAME);

export const mapStateToProps = (state: any) => ({
  getDocuments: (field: string) => selector(state, field),
  slotPins: selector(state, SLOT_PINS_FIELD) || {}, // EVERI-4005: stripped before submit
});

const reduxStateConnector = connect(mapStateToProps, {
  updateDocuments: (field: string, documents: any) =>
    change(VERIFICATION_FORM_NAME, field, documents),
  updateSlotPins: (pins: Record<string, string>) =>
    change(VERIFICATION_FORM_NAME, SLOT_PINS_FIELD, pins),
})(EmploymentMultidocPanel);

export default i18n.renderTranslation()(reduxStateConnector);
